package com.nonopn.skydome.common.app.pages.pdf

import kotlin.math.roundToInt

/** Overall lightning risk level of a site, shown on the summary page and on the recommendations page. */
enum class KeraunicRiskLevel { LOW, MODERATE, HIGH }

data class KeraunicRisk(val score: Int, val level: KeraunicRiskLevel)

/** Action of the recommendations page (PDF page 11 and the screen). */
enum class KeraunicRecommendationKind { INSTALL_ROD, GROUNDING, SURGE_PROTECTION, RISK_ASSESSMENT, ALERTS }

/** Card style and priority label: priority 1, priority 2, advice, Sky Sentinel. */
enum class KeraunicRecommendationPriority { CRITICAL, IMPORTANT, ADVISORY, SKY }

data class KeraunicRecommendation(val kind: KeraunicRecommendationKind, val priority: KeraunicRecommendationPriority)

/** Pure helpers of the Keraunic Report, kept free of HTML so they can be unit tested. */
internal object KeraunicReportLogic {
    private const val DIRECT_ZONE_KM = 1.0
    private const val HIGH_THRESHOLD = 75
    private const val MODERATE_THRESHOLD = 40

    /**
     * Single source of truth for the risk level: the score (0-100) combines the direct impacts (0-1 km),
     * the highest intensity observed within 2 km and the Nsg.
     */
    fun assessRisk(directCount: Int, maxKa: Double, nsg: Double): KeraunicRisk {
        val impactScore = (directCount.coerceIn(0, 100).toDouble() / 100 * 40).roundToInt()
        val intensityScore = (maxKa.coerceIn(0.0, 300.0) / 300 * 30).roundToInt()
        val nsgScore = (nsg.coerceIn(0.0, 5.0) / 5 * 30).roundToInt()
        val score = (impactScore + intensityScore + nsgScore).coerceIn(0, 100)
        val level = when {
            score >= HIGH_THRESHOLD -> KeraunicRiskLevel.HIGH
            score >= MODERATE_THRESHOLD -> KeraunicRiskLevel.MODERATE
            else -> KeraunicRiskLevel.LOW
        }
        return KeraunicRisk(score, level)
    }

    /**
     * Action plan of the recommendations page, in display order. Without a questionnaire, the site is assumed
     * to have neither a lightning rod nor surge protection.
     */
    fun recommendations(directCount: Int, hasRod: Boolean, hasSurge: Boolean): List<KeraunicRecommendation> = buildList {
        if (!hasRod && directCount > 0) {
            add(KeraunicRecommendation(KeraunicRecommendationKind.INSTALL_ROD, KeraunicRecommendationPriority.CRITICAL))
        }
        add(
            KeraunicRecommendation(
                KeraunicRecommendationKind.GROUNDING,
                if (hasRod) KeraunicRecommendationPriority.IMPORTANT else KeraunicRecommendationPriority.CRITICAL
            )
        )
        if (!hasSurge) {
            add(KeraunicRecommendation(KeraunicRecommendationKind.SURGE_PROTECTION, KeraunicRecommendationPriority.IMPORTANT))
        }
        add(KeraunicRecommendation(KeraunicRecommendationKind.RISK_ASSESSMENT, KeraunicRecommendationPriority.ADVISORY))
        add(KeraunicRecommendation(KeraunicRecommendationKind.ALERTS, KeraunicRecommendationPriority.SKY))
    }

    /**
     * Needle direction of the risk gauge, in degrees clockwise from 3 o'clock (screen/SVG convention):
     * 180 (left, score 0) to 360 (right, score 100), through 270 (top, score 50).
     */
    fun gaugeNeedleDegrees(score: Int): Double = 180.0 + score.coerceIn(0, 100) / 100.0 * 180.0

    /** Activity qualifier of the Nsg shown on the cover (impacts/km²/year). */
    fun nsgLevel(nsg: Double): KeraunicNsgLevel = when {
        nsg >= 2.0 -> KeraunicNsgLevel.HIGH
        nsg >= 1.0 -> KeraunicNsgLevel.MODERATE
        else -> KeraunicNsgLevel.LOW
    }

    /** CG- and CG+ shares (%) of a zone; they always add up to 100. */
    fun polarityPercents(negCount: Int, total: Int): Pair<Int, Int> {
        if (total <= 0) return 0 to 0
        val negPct = (negCount * 100.0 / total).roundToInt()
        return negPct to 100 - negPct
    }

    /** Highest intensity (kA) observed over both zones. */
    fun overallMaxKa(zone1: KeraunicZoneStats, zone2: KeraunicZoneStats): Double =
        maxOf(zone1.negMaxKa, zone1.posMaxKa, zone2.negMaxKa, zone2.posMaxKa)

    /** Impacts that really are in the direct danger zone (strictly under 1 km). */
    fun directImpacts(impacts: List<KeraunicTopImpact>): List<KeraunicTopImpact> =
        impacts.filter { it.distKm < DIRECT_ZONE_KM }

    /** The percentage is only meaningful when the server provided one. */
    fun hasPercent(impact: KeraunicTopImpact): Boolean = impact.percent > 0.0

    /** Polarity breakdown is available only when at least one polarity count is known. */
    fun hasPolarity(stats: KeraunicZoneStats): Boolean = stats.negCount > 0 || stats.posCount > 0

    // French name (as stored in the reference city table) -> English, Spanish, Portuguese
    private val CITY_NAMES: Map<String, Triple<String, String, String>> = mapOf(
        "Londres" to Triple("London", "Londres", "Londres"),
        "Barcelone" to Triple("Barcelona", "Barcelona", "Barcelona"),
        "Séville" to Triple("Seville", "Sevilla", "Sevilha"),
        "Lisbonne" to Triple("Lisbon", "Lisboa", "Lisboa"),
        "Porto" to Triple("Porto", "Oporto", "Porto"),
        "Rome" to Triple("Rome", "Roma", "Roma"),
        "Milan" to Triple("Milan", "Milán", "Milão"),
        "Naples" to Triple("Naples", "Nápoles", "Nápoles"),
        "Palerme" to Triple("Palermo", "Palermo", "Palermo"),
        "La Valette" to Triple("Valletta", "La Valeta", "Valeta"),
        "Athènes" to Triple("Athens", "Atenas", "Atenas"),
        "Istanbul" to Triple("Istanbul", "Estambul", "Istambul"),
        "Ankara" to Triple("Ankara", "Ankara", "Ancara"),
        "Munich" to Triple("Munich", "Múnich", "Munique"),
        "Francfort" to Triple("Frankfurt", "Fráncfort", "Frankfurt"),
        "Hambourg" to Triple("Hamburg", "Hamburgo", "Hamburgo"),
        "Berlin" to Triple("Berlin", "Berlín", "Berlim"),
        "Vienne" to Triple("Vienna", "Viena", "Viena"),
        "Zurich" to Triple("Zurich", "Zúrich", "Zurique"),
        "Genève" to Triple("Geneva", "Ginebra", "Genebra"),
        "Bruxelles" to Triple("Brussels", "Bruselas", "Bruxelas"),
        "Amsterdam" to Triple("Amsterdam", "Ámsterdam", "Amesterdão"),
        "Copenhague" to Triple("Copenhagen", "Copenhague", "Copenhaga"),
        "Stockholm" to Triple("Stockholm", "Estocolmo", "Estocolmo"),
        "Helsinki" to Triple("Helsinki", "Helsinki", "Helsínquia"),
        "Varsovie" to Triple("Warsaw", "Varsovia", "Varsóvia"),
        "Prague" to Triple("Prague", "Praga", "Praga"),
        "Bucarest" to Triple("Bucharest", "Bucarest", "Bucareste"),
        "Budapest" to Triple("Budapest", "Budapest", "Budapeste"),
        "Sofia" to Triple("Sofia", "Sofía", "Sófia"),
        "Belgrade" to Triple("Belgrade", "Belgrado", "Belgrado"),
        "Kiev" to Triple("Kyiv", "Kiev", "Kiev"),
        "Moscou" to Triple("Moscow", "Moscú", "Moscovo"),
        "Marseille" to Triple("Marseille", "Marsella", "Marselha"),
        "Bordeaux" to Triple("Bordeaux", "Burdeos", "Bordéus"),
        "Strasbourg" to Triple("Strasbourg", "Estrasburgo", "Estrasburgo"),
        "Dublin" to Triple("Dublin", "Dublín", "Dublin"),
        "Marrakech" to Triple("Marrakesh", "Marrakech", "Marraquexe"),
        "Alger" to Triple("Algiers", "Argel", "Argel"),
        "Tunis" to Triple("Tunis", "Túnez", "Tunes"),
        "Tripoli" to Triple("Tripoli", "Trípoli", "Trípoli"),
        "Le Caire" to Triple("Cairo", "El Cairo", "Cairo"),
        "Khartoum" to Triple("Khartoum", "Jartum", "Cartum"),
        "Addis-Abeba" to Triple("Addis Ababa", "Adís Abeba", "Adis Abeba"),
        "Le Cap" to Triple("Cape Town", "Ciudad del Cabo", "Cidade do Cabo"),
        "Johannesburg" to Triple("Johannesburg", "Johannesburgo", "Joanesburgo"),
        "Port-Louis" to Triple("Port Louis", "Port Louis", "Port Louis"),
        "Beyrouth" to Triple("Beirut", "Beirut", "Beirute"),
        "Amman" to Triple("Amman", "Ammán", "Amã"),
        "Riyad" to Triple("Riyadh", "Riad", "Riade"),
        "Djeddah" to Triple("Jeddah", "Yeda", "Jidá"),
        "Dubaï" to Triple("Dubai", "Dubái", "Dubai"),
        "Koweït" to Triple("Kuwait City", "Kuwait", "Kuwait"),
        "Téhéran" to Triple("Tehran", "Teherán", "Teerão"),
        "Bagdad" to Triple("Baghdad", "Bagdad", "Bagdá"),
        "New Delhi" to Triple("New Delhi", "Nueva Delhi", "Nova Deli"),
        "Calcutta" to Triple("Kolkata", "Calcuta", "Calcutá"),
        "Dacca" to Triple("Dhaka", "Daca", "Daca"),
        "Katmandou" to Triple("Kathmandu", "Katmandú", "Catmandu"),
        "Rangoun" to Triple("Yangon", "Yangón", "Yangon"),
        "Bangkok" to Triple("Bangkok", "Bangkok", "Banguecoque"),
        "Hanoï" to Triple("Hanoi", "Hanói", "Hanói"),
        "Hô Chi Minh-Ville" to Triple("Ho Chi Minh City", "Ciudad Ho Chi Minh", "Cidade de Ho Chi Minh"),
        "Singapour" to Triple("Singapore", "Singapur", "Singapura"),
        "Jakarta" to Triple("Jakarta", "Yakarta", "Jacarta"),
        "Manille" to Triple("Manila", "Manila", "Manila"),
        "Canton" to Triple("Guangzhou", "Cantón", "Cantão"),
        "Pékin" to Triple("Beijing", "Pekín", "Pequim"),
        "Taipei" to Triple("Taipei", "Taipéi", "Taipé"),
        "Séoul" to Triple("Seoul", "Seúl", "Seul"),
        "Tokyo" to Triple("Tokyo", "Tokio", "Tóquio"),
        "Osaka" to Triple("Osaka", "Osaka", "Osaca"),
        "Oulan-Bator" to Triple("Ulaanbaatar", "Ulán Bator", "Ulan Bator"),
        "Tachkent" to Triple("Tashkent", "Taskent", "Tashkent"),
        "Sydney" to Triple("Sydney", "Sídney", "Sydney"),
        "New York" to Triple("New York", "Nueva York", "Nova Iorque"),
        "San Francisco" to Triple("San Francisco", "San Francisco", "São Francisco"),
        "Los Angeles" to Triple("Los Angeles", "Los Ángeles", "Los Angeles"),
        "Montréal" to Triple("Montreal", "Montreal", "Montreal"),
        "Mexico" to Triple("Mexico City", "Ciudad de México", "Cidade do México"),
        "Panama" to Triple("Panama City", "Ciudad de Panamá", "Cidade do Panamá"),
        "Saint-Denis (La Réunion)" to Triple(
            "Saint-Denis (Réunion)", "Saint-Denis (Reunión)", "Saint-Denis (Reunião)"
        ),
    )

    /** Geographic names follow the language of the report; unknown names are kept unchanged. */
    fun localizeCity(frenchName: String, lang: PdfOutputLang): String {
        val names = CITY_NAMES[frenchName] ?: return frenchName
        return when (lang) {
            PdfOutputLang.FR -> frenchName
            PdfOutputLang.EN -> names.first
            PdfOutputLang.ES -> names.second
            PdfOutputLang.PT -> names.third
        }
    }
}
